import { Injectable, inject } from '@angular/core';
import { environment } from '../environments/environment';
import { AuthService } from './auth.service';
import { ErrorTrackingService, ErrorCategory, ErrorSeverity } from './error-tracking.service';
import { humanizeValidationErrorMessage } from '../utils/validation-messages.util';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  companies?: T[];
  items?: T[];
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private baseUrl = `${environment.apiBaseUrl}/v1`;
  private authService = inject(AuthService);
  private errorTracking = inject(ErrorTrackingService);
  
  // NOTE: GET response caching is intentionally disabled to avoid stale data.
  
  // Cache token expiration check to avoid parsing JWT on every request
  private tokenExpirationCache: { token: string; expiresAt: number } | null = null;
  private readonly TOKEN_EXPIRATION_CACHE_TTL = 60000; // Cache expiration check for 1 minute
  
  // Request batching for multiple simultaneous requests
  private requestQueue: Array<{ endpoint: string; options: RequestInit; resolve: (value: any) => void; reject: (error: any) => void }> = [];
  private batchTimer: any = null;
  private readonly BATCH_DELAY = 10; // 10ms delay to collect requests
  private readonly MAX_BATCH_SIZE = 10; // Maximum requests per batch
  
  // Request deduplication - track in-flight requests by endpoint + options
  private inFlightRequests = new Map<string, Promise<any>>();

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Get token once at the start
    let token: string | null = null;
    try {
      token = await this.authService.getToken();
    } catch (tokenError) {
      console.error('Failed to get token:', tokenError);
      // Continue without token - backend will return 401 if needed
    }
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    // Optimized token handling - check expiration only if token exists
    if (token) {
      // Use cached expiration check if available
      const isExpired = this.isTokenExpiredCached(token);
      if (isExpired) {
        // Token expired - try refresh before making request
        try {
          const refreshed = await this.authService.refreshAuthToken();
          if (refreshed) {
            token = await this.authService.getToken();
            // Clear expiration cache after refresh
            this.tokenExpirationCache = null;
          } else {
            throw new Error('Token expired. Please log out and log back in.');
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          throw new Error('Token expired. Please log out and log back in.');
        }
      }
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const method = options.method || 'GET';
    
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      // Extract rate limit headers
      const rateLimitHeaders = this.extractRateLimitHeaders(response);

      if (!response.ok) {
        // Handle rate limiting with user feedback
        if (response.status === 429) {
          const rateLimitError = await response.json().catch(() => ({ 
            error: 'Too many requests. Please try again later.',
            retryAfter: rateLimitHeaders.retryAfter 
          }));
          
          // Track rate limit error
          this.errorTracking.trackError(
            rateLimitError.error || 'Rate limit exceeded',
            ErrorCategory.NETWORK,
            ErrorSeverity.MEDIUM,
            {
              component: 'ApiService',
              action: 'rateLimitExceeded',
              endpoint,
              method,
              retryAfter: rateLimitError.retryAfter || rateLimitHeaders.retryAfter,
              limit: rateLimitHeaders.limit,
              remaining: rateLimitHeaders.remaining
            }
          );
          
          // Create error with rate limit info
          const error = new Error(rateLimitError.error || 'Too many requests. Please try again later.');
          (error as any).isRateLimit = true;
          (error as any).retryAfter = rateLimitError.retryAfter || rateLimitHeaders.retryAfter;
          (error as any).rateLimitInfo = rateLimitHeaders;
          throw error;
        }
        // Handle 401 specifically - token might be expired or invalid
        if (response.status === 401) {
          const error = await response.json().catch(() => ({ error: 'Unauthorized' }));
          console.error('Authentication failed:', error);
          
          // Check if error message indicates expired or invalid token
          const errorMessage = error.error || error.message || '';
          const errorCode = error.code || '';
          const isTokenExpired = errorMessage.includes('expired') || errorCode === 'TOKEN_EXPIRED';
          const isInvalidToken = errorMessage.includes('Invalid token') || errorMessage.includes('Unauthorized') || errorCode === 'INVALID_TOKEN';
          
          if (isTokenExpired || isInvalidToken) {
            console.warn('Token appears expired or invalid, attempting to refresh...');
            
            try {
              // Try to refresh the token using refresh token
              const refreshed = await this.authService.refreshAuthToken();
              
              if (refreshed) {
                // Get the new token and retry the request
                const newToken = await this.authService.getToken();
                
                if (newToken && newToken !== token) {
                  headers['Authorization'] = `Bearer ${newToken}`;
                  const retryResponse = await fetch(`${this.baseUrl}${endpoint}`, {
                    ...options,
                    headers,
                  });
                  if (retryResponse.ok) {
                    return retryResponse.json();
                  }
                  // If retry still fails after refresh, something else is wrong
                  const retryError = await retryResponse.json().catch(() => ({ error: 'Unauthorized' }));
                  throw new Error(`Request failed after token refresh: ${retryError.error || retryResponse.statusText}`);
                } else {
                  throw new Error('Token refresh succeeded but no new token available');
                }
              } else {
                // Refresh failed - token might be expired or refresh token invalid
                // Auth state is already cleared by handleTokenRefreshFailure()
                // Throw a specific error that can be caught by error handlers
                const error = new Error('Token expired. Please log out and log back in.');
                error.name = 'TokenRefreshError';
                throw error;
              }
            } catch (refreshError: any) {
              console.error('Token refresh failed:', refreshError);
              // Auth state should already be cleared by handleTokenRefreshFailure()
              // Re-throw the error so calling code can handle it appropriately
              if (refreshError.name === 'TokenRefreshError' || 
                  (refreshError.message && refreshError.message.includes('Token expired'))) {
                throw refreshError;
              }
              const error = new Error('Authentication failed. Please log out and log back in.');
              error.name = 'TokenRefreshError';
              throw error;
            }
          }
        }
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        const rawMessage = error.message || error.error || `HTTP ${response.status}`;
        const httpError = new Error(
          typeof rawMessage === 'string' ? humanizeValidationErrorMessage(rawMessage) : String(rawMessage)
        );
        
        // Track HTTP error
        this.errorTracking.trackHttpError(
          { status: response.status, statusText: response.statusText, error },
          endpoint,
          method,
          { component: 'ApiService', action: 'request' }
        );
        
        throw httpError;
      }

      return response.json();
    } catch (error: any) {
      // Handle rate limit errors specifically
      if (error?.isRateLimit) {
        // Already handled above, just rethrow
        throw error;
      }
      // Track error
      if (error instanceof Error && !error.message.includes('HTTP')) {
        // Only track non-HTTP errors here (HTTP errors already tracked above)
        this.errorTracking.trackHttpError(error, endpoint, method, {
          component: 'ApiService',
          action: 'request',
          errorType: error.name
        });
      }
      
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get<T>(endpoint: string, useCache: boolean = false, customTTL?: number): Promise<T> {
    void useCache;
    void customTTL;
    
    // Check for in-flight request deduplication
    const requestKey = `GET:${endpoint}`;
    const inFlightRequest = this.inFlightRequests.get(requestKey);
    if (inFlightRequest) {
      // Return the existing promise to share the same request
      return inFlightRequest as Promise<T>;
    }
    
    // Create new request promise
    const requestPromise = this.request<T>(endpoint, { method: 'GET' })
      .then((result) => {
        // Remove from in-flight requests
        this.inFlightRequests.delete(requestKey);
        return result;
      })
      .catch((error) => {
        // Remove from in-flight requests on error
        this.inFlightRequests.delete(requestKey);
        throw error;
      });
    
    // Store the in-flight request
    this.inFlightRequests.set(requestKey, requestPromise);
    
    return requestPromise;
  }

  /**
   * Batch multiple GET requests together for better performance
   * Useful when loading multiple resources simultaneously
   */
  async batchGet<T>(endpoints: string[], useCache: boolean = false): Promise<T[]> {
    void useCache;
    const fetchPromises = endpoints.map((endpoint, index) =>
      this.get<T>(endpoint, false).then(data => ({ index, data }))
    );

    const fetchedResults = await Promise.all(fetchPromises);
    return fetchedResults.sort((a, b) => a.index - b.index).map(r => r.data);
  }
  
  /**
   * No-op cache clear (GET caching disabled)
   */
  clearCache(pattern?: string): void {
    void pattern;
  }

  /** Clear in-flight dedup state and token expiry cache on session boundary. */
  clearSessionState(): void {
    this.inFlightRequests.clear();
    this.tokenExpirationCache = null;
  }

  async post<T>(endpoint: string, body: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async put<T>(endpoint: string, body: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async patch<T>(endpoint: string, body: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Check if JWT token is expired (with caching to avoid parsing on every request)
   */
  /**
   * Extract rate limit headers from response
   */
  private extractRateLimitHeaders(response: Response): {
    limit?: number;
    remaining?: number;
    reset?: number;
    retryAfter?: number;
  } {
    const headers = response.headers;
    return {
      limit: headers.get('X-RateLimit-Limit') ? parseInt(headers.get('X-RateLimit-Limit')!, 10) : undefined,
      remaining: headers.get('X-RateLimit-Remaining') ? parseInt(headers.get('X-RateLimit-Remaining')!, 10) : undefined,
      reset: headers.get('X-RateLimit-Reset') ? new Date(headers.get('X-RateLimit-Reset')!).getTime() : undefined,
      retryAfter: headers.get('Retry-After') ? parseInt(headers.get('Retry-After')!, 10) : undefined
    };
  }

  private isTokenExpiredCached(token: string): boolean {
    // Check if we have a cached expiration check for this token
    if (this.tokenExpirationCache && 
        this.tokenExpirationCache.token === token &&
        Date.now() < this.tokenExpirationCache.expiresAt) {
      // Use cached result - token hasn't changed and cache is still valid
      return false; // Cached check means it was valid
    }
    
    // Parse token and check expiration
    const isExpired = this.isTokenExpired(token);
    
    // Cache the result if token is valid
    if (!isExpired) {
      this.tokenExpirationCache = {
        token,
        expiresAt: Date.now() + this.TOKEN_EXPIRATION_CACHE_TTL
      };
    } else {
      // Clear cache if token is expired
      this.tokenExpirationCache = null;
    }
    
    return isExpired;
  }
  
  /**
   * Check if JWT token is expired
   */
  private isTokenExpired(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return true; // Invalid token format
      }
      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp;
      if (!exp) {
        return false; // No expiration claim, assume valid
      }
      // Add 5 minute buffer to refresh before actual expiration
      const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
      return Date.now() >= (exp * 1000 - bufferTime);
    } catch (error) {
      console.error('Failed to check token expiration:', error);
      return true; // Assume expired if we can't check
    }
  }

  /**
   * Get blob data (for PDF downloads, etc.)
   */
  async getBlob(url: string): Promise<Blob> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    const token = await this.authService.getToken();
    
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }

    return await response.blob();
  }
}
